/**
 * Data Tables Functionality
 * - Column Sorting
 * - Column Visibility Toggle
 * 
 * Usage: Add .js-data-table class to the .data-table container
 */

document.addEventListener('DOMContentLoaded', () => {
    const tables = document.querySelectorAll('.js-data-table');
    tables.forEach(initDataTable);
});

// ========================================================================
// Column Auto-Sizing
// ========================================================================

class ColumnAutoSizer {
    constructor(table, container) {
        this.table = table;
        this.container = container;
        this.canvas = null;
        this.ctx = null;
        this.cache = new Map(); // text|font -> width
        this.observer = null;
        this.hasMasterClass = container.classList.contains('data-table--auto-size');
    }

    async init() {
        // Wait for fonts to load before measuring
        if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
        }

        // Create hidden canvas for text measurement
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');

        // Initial measurement
        this.measure();

        // Listen for table refresh event
        this.container.addEventListener('table-refresh', () => {
            this.clearCache();
            this.measure();
        });

        // Watch for content changes with MutationObserver
        this.setupObserver();
    }

    measure() {
        // Skip if container is hidden
        if (!this.container.offsetParent) return;

        // Skip if sorting is in progress
        if (this.container._skipAutoSize) return;

        const thead = this.table.querySelector('thead');
        const tbody = this.table.querySelector('tbody');
        if (!thead || !tbody) return;

        const headers = Array.from(thead.querySelectorAll('th'));
        const widths = new Map(); // columnIndex -> width in pixels

        // Batch read phase: measure all columns
        headers.forEach((th, columnIndex) => {
            if (!this.shouldAutoSize(th, columnIndex)) return;

            const width = this.measureColumn(columnIndex);
            if (width) {
                widths.set(columnIndex, width);
            }
        });

        // Batch write phase: apply all widths at once
        this.applyWidths(widths);

        // Dispatch event
        this.container.dispatchEvent(new CustomEvent('auto-size-complete'));
    }

    measureColumn(columnIndex) {
        const thead = this.table.querySelector('thead');
        const tbody = this.table.querySelector('tbody');
        const headers = thead.querySelectorAll('th');
        const th = headers[columnIndex];

        // Measure header text
        const headerFont = this.getFontString(th);
        const headerText = th.textContent.trim();
        const headerWidth = this.measureText(headerText, headerFont);

        // Sample rows for performance
        const rows = this.getSampleRows(tbody);
        let maxCellWidth = 0;

        rows.forEach(row => {
            const cell = row.cells[columnIndex];
            if (!cell) return;

            const cellText = cell.textContent.trim();
            if (!cellText) return; // Skip empty cells

            const cellFont = this.getFontString(cell);
            const cellWidth = this.measureText(cellText, cellFont);
            maxCellWidth = Math.max(maxCellWidth, cellWidth);
        });

        // Get computed padding and border
        const thStyles = window.getComputedStyle(th);
        const paddingLeft = parseFloat(thStyles.paddingLeft) || 0;
        const paddingRight = parseFloat(thStyles.paddingRight) || 0;
        const borderLeft = parseFloat(thStyles.borderLeftWidth) || 0;
        const borderRight = parseFloat(thStyles.borderRightWidth) || 0;

        // Add space for sort icon if sortable
        const sortIconSpace = th.classList.contains('data-table__sortable') ? 20 : 0;

        // Calculate content width (max of header and cell widths)
        const contentWidth = Math.max(headerWidth, maxCellWidth);

        // Calculate final width (no extra breathing room needed - padding provides it)
        const totalWidth = Math.ceil(
            contentWidth + paddingLeft + paddingRight + borderLeft + borderRight + sortIconSpace
        );

        return totalWidth;
    }

    shouldAutoSize(th, columnIndex) {
        // Check for manual width classes (they override auto-sizing)
        if (this.hasManualWidth(th)) return false;

        // Check for hidden column
        if (th.classList.contains('dt-hidden')) return false;

        // Master class mode: auto-size all columns without manual widths
        if (this.hasMasterClass) return true;

        // Per-column mode: only auto-size if column has .dt-auto-size
        return th.classList.contains('dt-auto-size');
    }

    hasManualWidth(th) {
        // Check for manual width classes
        const manualWidthClasses = [
            'data-table__col-10', 'data-table__col-15', 'data-table__col-20',
            'data-table__col-25', 'data-table__col-30', 'data-table__col-40',
            'data-table__col-50', 'data-table__col-xs', 'data-table__col-sm',
            'data-table__col-md', 'data-table__col-lg', 'data-table__col-auto',
            'dt-col-10', 'dt-col-15', 'dt-col-20', 'dt-col-25', 'dt-col-30',
            'dt-col-40', 'dt-col-50', 'dt-col-xs', 'dt-col-sm', 'dt-col-md',
            'dt-col-lg', 'dt-col-auto'
        ];

        return manualWidthClasses.some(cls => th.classList.contains(cls));
    }

    measureText(text, font) {
        const cacheKey = `${text}|${font}`;

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        this.ctx.font = font;
        const metrics = this.ctx.measureText(text);
        const width = metrics.width;

        this.cache.set(cacheKey, width);
        return width;
    }

    getFontString(element) {
        const styles = window.getComputedStyle(element);
        return `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    }

    getSampleRows(tbody) {
        const allRows = Array.from(tbody.querySelectorAll('tr'));
        const totalRows = allRows.length;

        // Small tables: measure all rows
        if (totalRows <= 20) return allRows;

        // Medium/large tables: smart sampling
        const samples = [];

        // First 20 rows
        samples.push(...allRows.slice(0, Math.min(20, totalRows)));

        // Last 5 rows
        if (totalRows > 20) {
            samples.push(...allRows.slice(-5));
        }

        // Random middle rows
        const randomCount = totalRows > 100 ? 25 : 15;
        const middle = allRows.slice(20, -5);
        for (let i = 0; i < Math.min(randomCount, middle.length); i++) {
            const randomIndex = Math.floor(Math.random() * middle.length);
            samples.push(middle[randomIndex]);
        }

        // Remove duplicates, cap at 100
        return [...new Set(samples)].slice(0, 100);
    }

    applyWidths(widths) {
        const thead = this.table.querySelector('thead');
        const tbody = this.table.querySelector('tbody');
        const headers = thead.querySelectorAll('th');

        widths.forEach((width, columnIndex) => {
            const th = headers[columnIndex];
            th.style.width = `${width}px`;

            // Apply to all cells in this column
            const rows = tbody.querySelectorAll('tr');
            rows.forEach(row => {
                const cell = row.cells[columnIndex];
                if (cell) {
                    cell.style.width = `${width}px`;
                }
            });
        });
    }

    setupObserver() {
        const tbody = this.table.querySelector('tbody');
        if (!tbody) return;

        let debounceTimeout;
        this.observer = new MutationObserver(() => {
            // Debounce to prevent measurement thrashing
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                this.clearCache();
                this.measure();
            }, 150);
        });

        this.observer.observe(tbody, {
            childList: true,
            subtree: true,
            characterData: true
        });
    }

    clearCache() {
        this.cache.clear();
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

function initDataTable(container) {
    const table = container.querySelector('table');
    if (!table) return;

    // 1. Initialize Sorting
    const sortableHeaders = table.querySelectorAll('.data-table__sortable');
    sortableHeaders.forEach(header => {
        header.addEventListener('click', () => handleSort(table, header));
    });

    // 2. Initialize Column Visibility
    const toggleMenu = container.querySelector('[data-table-column-toggle]');
    if (toggleMenu) {
        initVisibilityMenu(table, toggleMenu);
    }

    // 3. Initialize Row Selection (if enabled)
    if (container.hasAttribute('data-table-selectable')) {
        initRowSelection(table);
    }

    // 4. Initialize Table Controls (refresh, sizing, column visibility)
    initTableControls(container, table);

    // 6. Initialize Column Auto-Sizing
    const hasMasterClass = container.classList.contains('data-table--auto-size');
    const hasColumnClasses = table.querySelectorAll('.dt-auto-size').length > 0;

    if (hasMasterClass || hasColumnClasses) {
        const autoSizer = new ColumnAutoSizer(table, container);
        container._autoSizer = autoSizer;
        autoSizer.init();
    }

    // 7. Initialize Group Collapse
    initGroupCollapse(container);
}

function handleSort(table, column) {
    const container = table.closest('.js-data-table');

    // Skip auto-sizing during sort (optimization)
    if (container && container._autoSizer) {
        container._skipAutoSize = true;
        setTimeout(() => {
            container._skipAutoSize = false;
        }, 200);
    }
    const tbody = table.querySelector('tbody');
    const tableHeaders = table.querySelectorAll('.data-table__sortable');
    const columnIndex = Array.from(column.parentNode.children).indexOf(column);
    
    // Determine new sort direction
    const currentSort = column.classList.contains('data-table__sorted-asc') ? 'asc' :
        column.classList.contains('data-table__sorted-desc') ? 'desc' : 'none';
    
    let newSort = 'asc';
    if (currentSort === 'asc') newSort = 'desc';
    else if (currentSort === 'desc') newSort = 'none';

    // Reset all headers
    tableHeaders.forEach(col => {
        col.classList.remove('data-table__sorted-asc', 'data-table__sorted-desc');
    });

    // Apply new sort class
    if (newSort !== 'none') {
        column.classList.add(`data-table__sorted-${newSort}`);
    }

    // Sort rows
    if (newSort === 'none') return;

    const rows = Array.from(tbody.querySelectorAll('tr'));
    
    rows.sort((a, b) => {
        const aCell = a.children[columnIndex];
        const bCell = b.children[columnIndex];
        
        // Handle missing cells
        const aVal = aCell ? aCell.innerText.trim() : '';
        const bVal = bCell ? bCell.innerText.trim() : '';

        const aNum = parseFloat(aVal.replace(/[^0-9.-]+/g,""));
        const bNum = parseFloat(bVal.replace(/[^0-9.-]+/g,""));

        // Check if numeric (and not an ID like #123)
        const isNumeric = !isNaN(aNum) && !isNaN(bNum) && !aVal.startsWith('#');

        if (isNumeric) {
            return newSort === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return newSort === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

    rows.forEach(row => tbody.appendChild(row));
}

function initVisibilityMenu(table, menu) {
    const headers = table.querySelectorAll('thead th');
    
    // Clear existing items
    menu.innerHTML = '';

    headers.forEach((th, index) => {
        const text = th.textContent.trim();
        // Skip empty headers (like actions column)
        if (!text) return;

        const item = document.createElement('label');
        item.className = 'dropdown-item';
        item.setAttribute('data-dropdown-keep-open', 'true');
        item.style.cursor = 'pointer';
        item.style.display = 'flex';
        item.style.alignItems = 'center';
        item.style.gap = '8px';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-check-input';
        checkbox.style.margin = '0';
        checkbox.checked = !th.classList.contains('dt-col-hidden');

        checkbox.addEventListener('change', (e) => {
            toggleColumn(table, index, e.target.checked);
        });

        const span = document.createElement('span');
        span.textContent = text;

        item.appendChild(checkbox);
        item.appendChild(span);
        menu.appendChild(item);
    });
}

function toggleColumn(table, index, isVisible) {
    const headers = table.querySelectorAll('thead th');
    const rows = table.querySelectorAll('tbody tr');

    if (headers[index]) {
        headers[index].classList.toggle('dt-col-hidden', !isVisible);
    }

    rows.forEach(row => {
        if (row.children[index]) {
            row.children[index].classList.toggle('dt-col-hidden', !isVisible);
        }
    });

    // Re-measure auto-sized columns when showing a column
    if (isVisible) {
        const container = table.closest('.js-data-table');
        if (container && container._autoSizer) {
            container._autoSizer.clearCache();
            container._autoSizer.measure();
        }
    }
}

function initRowSelection(table) {
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    tbody.addEventListener('click', (e) => {
        const row = e.target.closest('tr');
        if (!row) return;

        // Don't select if clicking on buttons, links, or inputs
        if (e.target.closest('button, a, input, select, textarea')) return;

        // Toggle selection
        row.classList.toggle('table-row--selected');
    });
}


function initTableControls(container, table) {
    // Refresh button - resets table to default state
    const refreshBtn = container.querySelector('[data-table-refresh]');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
            // Reset sorting
            const sortableHeaders = table.querySelectorAll('.data-table__sortable');
            sortableHeaders.forEach(header => {
                header.classList.remove('data-table__sorted-asc', 'data-table__sorted-desc');
            });

            // Reset row selection
            const selectedRows = table.querySelectorAll('.table-row--selected');
            selectedRows.forEach(row => {
                row.classList.remove('table-row--selected');
            });

            // Could also dispatch a custom event for external listeners
            container.dispatchEvent(new CustomEvent('table-refresh'));
        });
    }

    // Sizing toggle - switches between default and condensed
    const sizeButtons = container.querySelectorAll('[data-table-size]');
    sizeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const size = btn.getAttribute('data-table-size');

            // Remove all size classes
            container.classList.remove('data-table--condensed', 'data-table--sm', 'data-table--lg');

            // Add the selected size class
            if (size === 'condensed') {
                container.classList.add('data-table--condensed');
            } else if (size === 'small') {
                container.classList.add('data-table--sm');
            } else if (size === 'large') {
                container.classList.add('data-table--lg');
            }
            // 'default' means no modifier class

            // Update active state with checkmarks
            sizeButtons.forEach(b => {
                const checkIcon = b.querySelector('.fa-check');
                if (checkIcon) {
                    checkIcon.style.opacity = '0';
                }
                b.classList.remove('active');
            });

            const checkIcon = btn.querySelector('.fa-check');
            if (checkIcon) {
                checkIcon.style.opacity = '1';
            }
            btn.classList.add('active');

            // Re-measure auto-sized columns after size change (font size affects width)
            if (container._autoSizer) {
                container._autoSizer.clearCache();
                container._autoSizer.measure();
            }
        });
    });

    // Initialize default size button as active on load
    const defaultSizeBtn = Array.from(sizeButtons).find(btn =>
        btn.getAttribute('data-table-size') === 'default'
    );
    if (defaultSizeBtn) {
        const checkIcon = defaultSizeBtn.querySelector('.fa-check');
        if (checkIcon) {
            checkIcon.style.opacity = '1';
        }
    }
}

// ========================================================================
// Group Collapse — click group headers to collapse/expand section rows
// ========================================================================
// Usage: add data-collapse-group to a .data-table__group-header or
//        .data-table__subgroup-header <tr>. Clicking toggles all rows
//        between this header and the next header of the same level.

function initGroupCollapse(container) {
    const table = container.querySelector('table');
    if (!table) return;

    const headers = table.querySelectorAll('[data-collapse-group]');
    headers.forEach(header => {
        header.setAttribute('tabindex', '0');
        header.setAttribute('role', 'button');
        header.setAttribute('aria-expanded', 'true');
        header.addEventListener('click', (e) => {
            // Don't toggle when clicking links, buttons, or inputs inside the header
            if (e.target.closest('a, button, input, select, textarea')) return;
            toggleGroup(header);
        });
        header.addEventListener('keydown', (e) => {
            // Keys pressed on a focused link or button inside the header belong to it
            if (e.target !== header) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleGroup(header);
            }
        });
    });
}

function toggleGroup(header) {
    const isCollapsed = header.classList.toggle('is-collapsed');
    header.setAttribute('aria-expanded', String(!isCollapsed));
    const isGroupHeader = header.classList.contains('data-table__group-header');

    // Use table.rows to walk all rows across tbody boundaries
    // (entries wrapped in individual <tbody> for HTMX swap targeting)
    const table = header.closest('table');
    const rows = table.rows;
    const startIdx = Array.prototype.indexOf.call(rows, header);

    for (let i = startIdx + 1; i < rows.length; i++) {
        const row = rows[i];

        // Stop at the next group header (always)
        if (row.classList.contains('data-table__group-header')) break;

        // If collapsing a subgroup header, stop at the next subgroup header too
        if (!isGroupHeader && row.classList.contains('data-table__subgroup-header')) break;

        row.classList.toggle('group-row--hidden', isCollapsed);

        // When expanding a group header, reset any nested subgroup collapse state
        if (!isCollapsed && isGroupHeader && row.classList.contains('is-collapsed')) {
            row.classList.remove('is-collapsed');
        }
    }
}

// Export for manual initialization if needed
window.initDataTable = initDataTable;

// Global API for manual column auto-sizing
window.autoSizeTableColumns = function(tableOrContainer) {
    const container = tableOrContainer.classList?.contains('data-table')
        ? tableOrContainer.closest('.js-data-table')
        : tableOrContainer;

    if (container?._autoSizer) {
        container._autoSizer.measure();
    }
};

// ========================================================================
// Stacked Tables — mirror dt-stack-* roles from <th> onto body cells
// ========================================================================
// .data-table--stacked lays each row out as a list item below the phone
// breakpoint (see tables.less). The CSS needs the role on the <td>, but the
// template only marks the <th>; copy each header's dt-stack-* classes onto
// the cell at the same index, then mark the wrapper .is-stacked — the CSS
// only applies once that class is present, so a table whose JS never ran
// stays a normal scrolling table instead of hiding its cells. Runs once at
// load — rows added later need the classes in their own markup, or a call
// to window.mirrorStackRoles(table). Rows whose cell count differs from the
// header (colspan group headers, empty states) are skipped.

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.data-table--stacked table').forEach(mirrorStackRoles);
});

function mirrorStackRoles(table) {
    const headers = Array.from(table.querySelectorAll(':scope > thead > tr:last-child > th'));
    if (!headers.length) return;

    const roles = headers.map(th =>
        Array.from(th.classList).filter(c => c.startsWith('dt-stack-')));

    table.querySelectorAll(':scope > tbody > tr, :scope > tfoot > tr').forEach(row => {
        if (row.children.length !== headers.length) return;
        roles.forEach((classes, i) => {
            if (classes.length) row.children[i].classList.add(...classes);
        });
    });

    table.closest('.data-table--stacked')?.classList.add('is-stacked');
}

window.mirrorStackRoles = mirrorStackRoles;
